import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  Edit2, MapPin, GraduationCap, Briefcase, Heart, Shield, Crown,
  BookHeart, Send, Check, Home, Users, MoreVertical, Flag, UserX,
  Star, Eye, Lock, Phone, ShieldCheck, LogOut,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { profileApi } from '../../api/profile'
import { interestsApi } from '../../api/interests'
import { shortlistApi } from '../../api/shortlist'
import { reportApi } from '../../api/report'
import { authApi } from '../../api/auth'
import { useAuthStore } from '../../store/authStore'

const REPORT_REASONS = [
  { value: 'ASKED_FOR_MONEY', label: 'Asked me for money' },
  { value: 'SCAM', label: 'Scam / Fraud' },
  { value: 'FAKE_PROFILE', label: 'Fake Profile' },
  { value: 'INAPPROPRIATE_PHOTO', label: 'Inappropriate Photos' },
  { value: 'HARASSMENT', label: 'Harassment' },
  { value: 'SPAM', label: 'Spam' },
  { value: 'OTHER', label: 'Other' },
]

export default function ProfilePage() {
  const { id } = useParams()
  const isOwn = !id
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { clearAuth, accessToken } = useAuthStore()
  const [interestMsg, setInterestMsg] = useState('')

  const handleLogout = async () => {
    if (!window.confirm("Log out? You'll need your mobile number and OTP to sign back in.")) return
    try {
      if (accessToken) await authApi.logout(accessToken)
    } catch { /* ignore */ } finally {
      clearAuth()
      navigate('/login')
      toast.success('Logged out')
    }
  }
  const [showMsgBox, setShowMsgBox] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [lightbox, setLightbox] = useState<string | null>(null)
  // Stale/removed photo URL → show the initial instead of the browser's broken-image glyph.
  const [heroBroken, setHeroBroken] = useState(false)
  const [reportReason, setReportReason] = useState('FAKE_PROFILE')
  const [reportDesc, setReportDesc] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)


  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', id ?? 'me'],
    queryFn: () =>
      isOwn
        ? profileApi.getMyProfile().then((r) => r.data.data)
        : profileApi.getProfile(id!).then((r) => r.data.data),
  })

  // Record profile view (fire and forget)
  useEffect(() => {
    if (!isOwn && id && profile) {
      profileApi.recordView(id)
    }
  }, [isOwn, id, profile])

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const { data: slStatus } = useQuery({
    queryKey: ['shortlist-status', id],
    // Endpoint returns { isShortlisted: boolean } — read the flag, not the object
    // (an object is always truthy, which pinned the button to "Shortlisted").
    queryFn: () => shortlistApi.isShortlisted(id!).then((r) => (r.data.data as any)?.isShortlisted ?? false),
    enabled: !!id && !isOwn,
  })

  // Existing interest to this profile (to show "Sent"/"Matched" instead of the send button)
  const { data: sentInterests } = useQuery({
    queryKey: ['interests', 'sent'],
    queryFn: () => interestsApi.getSent().then((r) => r.data.data),
    enabled: !isOwn,
  })
  const existingInterest = !isOwn ? sentInterests?.find((i) => i.receiverProfileId === id) : undefined

  const interestMut = useMutation({
    mutationFn: () => interestsApi.sendInterest(id!, interestMsg || undefined),
    onSuccess: () => {
      toast.success('Interest sent!')
      setShowMsgBox(false)
      qc.invalidateQueries({ queryKey: ['interests'] })
    },
    onError: (e: any) => {
      const status = e?.response?.status
      if (status === 402 || e?.response?.data?.code === 'INTEREST_LIMIT_REACHED') {
        toast((t) => (
          <span className="flex items-center gap-2">
            Monthly limit reached.{' '}
            <a href="/subscription" className="font-bold text-maroon-700 underline" onClick={() => toast.dismiss(t.id)}>
              Upgrade to Premium
            </a>
          </span>
        ), { duration: 5000 })
      } else {
        toast.error(e?.response?.data?.message ?? 'Could not send interest')
      }
    },
  })

  const shortlistMut = useMutation({
    mutationFn: () => slStatus ? shortlistApi.remove(id!) : shortlistApi.add(id!),
    onSuccess: () => {
      toast.success(slStatus ? 'Removed from shortlist' : 'Added to shortlist')
      qc.invalidateQueries({ queryKey: ['shortlist-status', id] })
      qc.invalidateQueries({ queryKey: ['shortlist'] })
    },
  })

  const blockMut = useMutation({
    mutationFn: () => profileApi.blockProfile(id!),
    onSuccess: () => {
      toast.success('Profile blocked')
      setMenuOpen(false)
      navigate('/matches')
    },
    onError: () => toast.error('Failed to block'),
  })

  const reportMut = useMutation({
    mutationFn: () => reportApi.submit(id!, reportReason, reportDesc || undefined),
    onSuccess: () => {
      toast.success('Report submitted. Thank you.')
      setShowReportModal(false)
      setReportDesc('')
    },
    onError: () => toast.error('Failed to submit report'),
  })

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="h-72 animate-pulse bg-ivory-100 rounded-3xl" />
        <div className="h-36 animate-pulse bg-ivory-100 rounded-2xl" />
        <div className="h-24 animate-pulse bg-ivory-100 rounded-2xl" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-ink-400 px-4">
        {isOwn ? (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-ivory-100 border border-gold-500/30 flex items-center justify-center mx-auto mb-4">
              <Heart strokeWidth={1.5} className="w-8 h-8 text-maroon-700/50" />
            </div>
            <p className="font-display font-semibold text-xl text-ink-900 mb-1">No profile yet</p>
            <p className="text-sm text-ink-400 mb-5">Create your profile to start finding matches</p>
            <Link to="/profile/edit" className="btn-primary inline-flex items-center gap-2">
              <Edit2 size={15} /> Create Profile
            </Link>
          </div>
        ) : (
          <p>Profile not found</p>
        )}
      </div>
    )
  }

  const age = profile.dateOfBirth
    ? Math.floor((Date.now() - new Date(profile.dateOfBirth).getTime()) / 31557600000)
    : null

  const locationParts = [profile.nativeVillage, profile.nativeDistrict, profile.nativeState].filter(Boolean)

  // Completeness nudge sections for own profile
  const missingItems = isOwn ? [
    !profile.avatarUrl && 'Add a profile photo',
    !profile.aboutMe && 'Write an About Me',
    !profile.heightCm && 'Add your height',
    !profile.educationLevel && 'Add education details',
    !profile.fatherName && 'Add family details',
  ].filter(Boolean) : []

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

      {/* Hero Card */}
      <div>
        <div className="relative h-[400px] mx-1 rounded-3xl overflow-hidden border border-gold-500/50 shadow-float bg-ivory-100">
          {profile.photosPrivate ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-ivory-100 gap-3">
              <div className="w-16 h-16 rounded-full border border-gold-500/40 flex items-center justify-center">
                <Lock size={26} strokeWidth={1.5} className="text-gold-600" />
              </div>
              <p className="text-sm font-medium text-ink-400">Photos visible after interest is accepted</p>
            </div>
          ) : profile.avatarUrl && !heroBroken ? (
            <>
              <img src={profile.avatarUrl} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-70 saturate-[0.9]" />
              <img src={profile.avatarUrl} alt="" onError={() => setHeroBroken(true)} onClick={() => setLightbox(profile.avatarUrl!)} className="relative w-full h-full object-contain saturate-[0.95] cursor-zoom-in" />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-7xl text-maroon-700/30">
              {profile.fullName?.charAt(0) ?? '?'}
            </div>
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 55%, #2C0812E6)' }} />
          <div className="absolute top-3 right-3 flex gap-1.5 items-center">
            {profile.isPremium && (
              <span className="bg-ivory-25/90 text-gold-600 border border-gold-500/60 text-[10px] font-bold tracking-wide px-2 py-1 rounded-full flex items-center gap-1">
                <Crown size={10} /> PREMIUM
              </span>
            )}
            {profile.isVerified && (
              <span className="bg-ivory-25/90 text-success border border-success/40 text-[10px] font-bold tracking-wide px-2 py-1 rounded-full flex items-center gap-1">
                <Shield size={10} /> VERIFIED
              </span>
            )}
            {profile.govtIdVerified && (
              <span title="Government ID verified by our team" className="bg-emerald-700 text-ivory-25 text-[10px] font-bold tracking-wide px-2 py-1 rounded-full flex items-center gap-1">
                <ShieldCheck size={10} /> ID VERIFIED
              </span>
            )}
            {/* Three-dot menu for other profiles */}
            {!isOwn && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="w-8 h-8 bg-black/35 hover:bg-black/55 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <MoreVertical size={16} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-10 bg-ivory-25 rounded-2xl shadow-float border border-ivory-200 py-1 min-w-[160px] z-50 animate-scale-in">
                    <button
                      onClick={() => { setMenuOpen(false); setShowReportModal(true) }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm font-semibold text-ink-600 hover:bg-ivory-100"
                    >
                      <Flag size={15} className="text-pending" /> Report
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Block this profile? They will no longer appear in your browse.')) {
                          blockMut.mutate()
                        }
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm font-semibold text-danger hover:bg-ivory-100"
                    >
                      <UserX size={15} /> Block
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5 text-center">
            <h1 className="font-display font-semibold text-[26px] text-champagne-bright">
              {profile.fullName ?? 'Member'}
              {age && <span className="text-champagne/80">, {age}</span>}
            </h1>
            {(profile.fullNameHindi || locationParts.length > 0) && (
              <p className="text-gold-500 text-[10px] font-semibold uppercase tracking-[0.28em] mt-1.5">
                {[profile.fullNameHindi, profile.nativeDistrict, profile.gotraName].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>

        <div className="px-1 pt-5">
          {/* Completion bar (own profile) */}
          {isOwn && (
            <div className="mb-4">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-ink-400 font-medium">Profile completion</span>
                <span className="text-maroon-700 font-semibold">{profile.profileCompletePct}%</span>
              </div>
              <div className="h-1.5 bg-ivory-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gold-500 rounded-full transition-all duration-500"
                  style={{ width: `${profile.profileCompletePct}%` }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          {isOwn ? (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Link to="/profile/edit" className="btn-secondary flex-1 flex items-center justify-center gap-2 text-sm">
                  <Edit2 size={14} /> Edit Profile
                </Link>
                <Link to="/profile/viewers" className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] py-3 px-4 rounded-[14px] border-[1.5px] border-ivory-300 text-ink-400 hover:border-maroon-700 hover:text-maroon-700 transition-all">
                  <Eye size={15} /> Viewers
                </Link>
              </div>
              {/* Logout — discoverable on mobile, where the sidebar (and its logout) is hidden */}
              <button
                onClick={handleLogout}
                className="flex items-center justify-center gap-2 text-[12px] font-semibold uppercase tracking-[0.12em] py-3 px-4 rounded-[14px] border-[1.5px] border-ivory-300 text-ink-400 hover:border-danger hover:text-danger transition-all"
              >
                <LogOut size={15} /> Log out
              </button>
              <Link to="/safety" className="flex items-center justify-center gap-1.5 text-[12px] font-semibold text-ink-400 hover:text-maroon-700 transition-colors pt-1">
                <ShieldCheck size={14} /> Safety Center
              </Link>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => shortlistMut.mutate()}
                disabled={shortlistMut.isPending}
                className={`flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] py-3 px-4 rounded-[14px] border-[1.5px] transition-all ${
                  slStatus
                    ? 'text-maroon-700 border-maroon-700 bg-maroon-700/5'
                    : 'text-ink-400 border-ivory-300 hover:border-maroon-700 hover:text-maroon-700'
                }`}
              >
                <BookHeart size={15} />
                {slStatus ? 'Shortlisted' : 'Shortlist'}
              </button>
              {existingInterest?.status === 'ACCEPTED' ? (
                <Link to="/chat" className="flex-1 flex items-center justify-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] py-3 px-4 rounded-[14px] text-success border-[1.5px] border-success/50 bg-success/5">
                  <Check size={14} /> Matched — Open Chat
                </Link>
              ) : existingInterest ? (
                <div className="flex-1 flex items-center justify-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] py-3 px-4 rounded-[14px] bg-ivory-100 text-ink-400 border border-ivory-200">
                  <Check size={14} /> Interest Sent
                </div>
              ) : !showMsgBox ? (
                <button
                  onClick={() => setShowMsgBox(true)}
                  className="btn-primary flex items-center gap-1.5 text-sm flex-1"
                >
                  <Send size={14} /> Send Interest
                </button>
              ) : (
                <div className="flex-1 flex flex-col gap-2">
                  <textarea
                    className="input text-sm resize-none"
                    rows={2}
                    placeholder="Add a personal message (optional)…"
                    value={interestMsg}
                    onChange={(e) => setInterestMsg(e.target.value)}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => interestMut.mutate()}
                      disabled={interestMut.isPending}
                      className="btn-primary flex items-center gap-1.5 text-sm py-2 px-4"
                    >
                      <Check size={13} /> {interestMut.isPending ? 'Sending…' : 'Send'}
                    </button>
                    <button onClick={() => setShowMsgBox(false)} className="btn-secondary text-sm py-2 px-4">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Completeness nudges (own profile, < 80%) */}
      {isOwn && missingItems.length > 0 && profile.profileCompletePct < 80 && (
        <div className="card p-4 border-l-2 border-l-gold-500">
          <p className="kicker mb-2">Complete your profile</p>
          <ul className="space-y-1">
            {missingItems.slice(0, 4).map((item) => (
              <li key={item as string} className="flex items-center gap-2 text-sm text-ink-600">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <Link to="/profile/edit" className="inline-flex items-center gap-1.5 text-xs font-bold text-maroon-700 mt-3 hover:underline">
            <Edit2 size={11} /> Complete now
          </Link>
        </div>
      )}

      {/* Details */}
      <div className="card p-5">
        <h2 className="section-title mb-4">Details</h2>
        <div className="space-y-3">
          {profile.phone && (
            <DetailRow icon={Phone} label="Phone" value={profile.phone} />
          )}
          {locationParts.length > 0 && (
            <DetailRow icon={MapPin} label="From" value={locationParts.join(', ')} />
          )}
          {profile.currentCity && (
            <DetailRow icon={Home} label="Lives in" value={profile.currentCity} />
          )}
          {profile.heightCm && (
            <DetailRow icon={Star} label="Height" value={cmToFeet(profile.heightCm)} />
          )}
          {profile.educationLevel && (
            <DetailRow icon={GraduationCap} label="Education" value={profile.educationLevel.replace(/_/g, ' ')} />
          )}
          {profile.occupation && (
            <DetailRow
              icon={Briefcase}
              label="Occupation"
              value={[profile.occupationDetail, profile.companyName].filter(Boolean).join(' · ') || profile.occupation.replace(/_/g, ' ')}
            />
          )}
          {profile.annualIncomeLpa && (
            <DetailRow icon={Briefcase} label="Annual Income" value={`₹${profile.annualIncomeLpa} LPA`} />
          )}
          {/* Gotra is community-critical — always show all three (self / mother / grandmother). */}
          <DetailRow icon={Heart} label="Gotra" value={profile.gotraName || profile.gotraCustom || 'Not specified'} />
          <DetailRow icon={Heart} label="Mother's Gotra" value={profile.motherGotraName || profile.motherGotraCustom || 'Not specified'} />
          <DetailRow icon={Heart} label="Grandmother's Gotra" value={profile.grandmotherGotraName || profile.grandmotherGotraCustom || 'Not specified'} />
          {profile.maritalStatus && (
            <DetailRow icon={Users} label="Marital Status" value={profile.maritalStatus.replace(/_/g, ' ')} />
          )}
        </div>
      </div>

      {/* Family Details */}
      {(profile.fatherName || profile.motherName || profile.siblingsCount != null || profile.familyType) && (
        <div className="card p-5">
          <h2 className="section-title mb-4">Family</h2>
          <div className="space-y-3">
            {profile.fatherName && (
              <DetailRow icon={Users} label="Father" value={[profile.fatherName, profile.fatherOccupation].filter(Boolean).join(' · ')} />
            )}
            {profile.motherName && (
              <DetailRow icon={Users} label="Mother" value={profile.motherName} />
            )}
            {profile.siblingsCount != null && (
              <DetailRow icon={Users} label="Siblings" value={String(profile.siblingsCount)} />
            )}
            {profile.familyType && (
              <DetailRow icon={Home} label="Family Type" value={profile.familyType.replace(/_/g, ' ')} />
            )}
            {profile.landAcres && (
              <DetailRow icon={MapPin} label="Land" value={`${profile.landAcres} acres`} />
            )}
          </div>
        </div>
      )}

      {/* Horoscope / Kundali */}
      {(profile.rashi || profile.nakshatra || profile.birthTime || profile.birthPlace ||
        (profile.manglik && profile.manglik !== 'NO')) && (
        <div className="card p-5">
          <h2 className="section-title mb-4">Horoscope</h2>
          <div className="space-y-3">
            {profile.manglik && profile.manglik !== 'NO' && (
              <DetailRow icon={Star} label="Manglik" value={profile.manglik === 'PARTIAL' ? 'Anshik (Partial)' : 'Yes'} />
            )}
            {profile.rashi && <DetailRow icon={Star} label="Rashi" value={profile.rashi} />}
            {profile.nakshatra && <DetailRow icon={Star} label="Nakshatra" value={profile.nakshatra} />}
            {profile.birthTime && <DetailRow icon={Star} label="Time of Birth" value={profile.birthTime} />}
            {profile.birthPlace && <DetailRow icon={Star} label="Place of Birth" value={profile.birthPlace} />}
          </div>
        </div>
      )}

      {/* Siblings */}
      {(() => {
        let list: { name: string; profession?: string; maritalStatus?: string }[] = []
        try { list = JSON.parse(profile.siblings || '[]') } catch { /* ignore */ }
        return list.length > 0 ? (
          <div className="card p-5">
            <h2 className="section-title mb-4">Siblings</h2>
            <div className="space-y-2">
              {list.map((s, i) => (
                <DetailRow key={i} icon={Users} label={s.name}
                  value={[s.profession, s.maritalStatus === 'MARRIED' ? 'Married' : 'Unmarried'].filter(Boolean).join(' · ')} />
              ))}
            </div>
          </div>
        ) : null
      })()}


      {/* About */}
      {(profile.aboutMe || profile.partnerExpectations) && (
        <div className="card p-5 space-y-4">
          {profile.aboutMe && (
            <div>
              <h2 className="section-title mb-3">About Me</h2>
              <p className="text-ink-600 text-sm leading-relaxed">{profile.aboutMe}</p>
            </div>
          )}
          {profile.partnerExpectations && (
            <div>
              <h2 className="section-title mb-3">Partner Expectations</h2>
              <p className="text-ink-600 text-sm leading-relaxed">{profile.partnerExpectations}</p>
            </div>
          )}
        </div>
      )}

      {/* Gallery */}
      {!profile.photosPrivate && profile.galleryUrls && profile.galleryUrls.length > 0 && (
        <div className="card p-5">
          <h2 className="section-title mb-4">Photos</h2>
          <div className="grid grid-cols-3 gap-2">
            {profile.galleryUrls.map((url, i) => (
              <img key={i} src={url} alt="" onError={(e) => { e.currentTarget.style.display = 'none' }} onClick={() => setLightbox(url)} className="aspect-[3/4] object-cover rounded-2xl cursor-zoom-in" />
            ))}
          </div>
        </div>
      )}

      {/* Photo lightbox — full-screen view, click anywhere to close */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
          <button
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-xl leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-maroon-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-ivory-25 border border-gold-500/30 rounded-3xl w-full max-w-md p-6 space-y-4 animate-scale-in">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-ivory-100 flex items-center justify-center">
                <Flag size={15} className="text-danger" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-ink-900">Report Profile</h3>
                <p className="text-xs text-ink-400">Help us keep the community safe</p>
              </div>
            </div>
            <div>
              <label className="label">Reason</label>
              <select className="input" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                {REPORT_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Details <span className="normal-case font-normal text-ink-400/70 tracking-normal">(optional)</span></label>
              <textarea
                className="input resize-none"
                rows={3}
                placeholder="Describe the issue…"
                value={reportDesc}
                onChange={(e) => setReportDesc(e.target.value)}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => reportMut.mutate()}
                disabled={reportMut.isPending}
                className="btn-primary flex-1 py-3"
              >
                {reportMut.isPending ? 'Submitting…' : 'Submit Report'}
              </button>
              <button
                onClick={() => setShowReportModal(false)}
                className="btn-secondary px-5 py-3"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function cmToFeet(cm: number) {
  const totalIn = Math.round(cm / 2.54)
  const ft = Math.floor(totalIn / 12)
  const inch = totalIn % 12
  return `${ft}'${inch}" (${cm} cm)`
}

function DetailRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="w-7 h-7 rounded-full bg-ivory-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={12} strokeWidth={1.75} className="text-maroon-700" />
      </div>
      <div>
        <p className="text-[10px] text-ink-400 font-semibold uppercase tracking-wider">{label}</p>
        <p className="text-ink-900 font-medium">{value}</p>
      </div>
    </div>
  )
}
